import type { MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { cn } from '../../../lib/cn'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import type { ResumoChamado } from '../../../types/chamado'
import { BarraDeSla } from '../BarraDeSla'
import { EtiquetaDeOrigemDaDemanda, EtiquetaDeProcedencia, EtiquetaDeSla } from '../etiquetas'
import { SO_A_PARTIR_DE_LARGA, SO_A_PARTIR_DE_MEDIA, SO_EM_TELA_MUITO_LARGA } from './colunas'
import { Fechamento, GmudVinculada, Julgamento, RamoDaArvore } from './marcadores'

const CELULA = 'px-4 py-3 align-middle text-sm text-slate-700'

/**
 * Um chamado na tabela.
 *
 * A linha inteira abre o detalhe, mas quem faz isso é o `onClick` do `<tr>` — sem `role` e sem
 * `tabIndex`. O caminho do teclado continua sendo o link do número, e só ele: dar foco também à
 * linha faria quem navega por teclado passar duas vezes por cada chamado, e transformar a `<tr>` em
 * `role="link"` custaria a semântica de linha de tabela que o leitor de tela usa para anunciar
 * "Prazo: prazo estourado".
 *
 * Nenhuma linha ganha cor de fundo por situação de prazo. A cor mora na etiqueta e na barra de
 * consumo, as duas com texto ao lado: pintar a linha inteira de vermelho comunicaria por cor
 * sozinha — invisível para quem não distingue vermelho de cinza — e faria a lista parecer um
 * relatório de erros, quando prazo estourado é um fato contratual, não um defeito do sistema.
 *
 * Assunto é cortado em duas linhas, com o texto inteiro no `title`. Entidade e grupo mostram só o
 * ramo final da árvore do e-Niterói (a raiz é a mesma em todas as linhas) — ver `RamoDaArvore`.
 *
 * @param props a linha da listagem
 * @returns a linha da tabela
 */
export function LinhaDeChamado({ chamado }: { chamado: ResumoChamado }) {
  const navegar = useNavigate()

  function aoClicar(evento: MouseEvent<HTMLTableRowElement>) {
    // O link do número já navega sozinho. Sem esta guarda, o clique nele sobe até aqui e empilha
    // uma segunda entrada no histórico — e o "voltar" passa a exigir dois cliques.
    if (evento.target instanceof Element && evento.target.closest('a')) {
      return
    }
    // Selecionar o número para colar num e-mail é o que o fiscal faz o dia inteiro, e não é um
    // pedido para abrir o chamado.
    if (window.getSelection()?.toString()) {
      return
    }
    navegar(`/chamados/${chamado.id}`)
  }

  return (
    <tr
      onClick={aoClicar}
      className={cn(
        'cursor-pointer border-b border-slate-100 transition-colors',
        'odd:bg-white even:bg-slate-50/60 hover:bg-niteroi-50',
      )}
    >
      <td className={cn(CELULA, 'tabular-nums')}>
        <Link
          to={`/chamados/${chamado.id}`}
          className="font-semibold text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
        >
          {chamado.idExterno}
        </Link>
      </td>
      <td className={cn(CELULA, 'min-w-[14rem]')}>
        <span className="flex flex-wrap items-center gap-2">
          <span className="line-clamp-2 max-w-sm break-words" title={chamado.titulo}>
            {chamado.titulo}
          </span>
          <EtiquetaDeOrigemDaDemanda origem={chamado.origemDemanda} />
        </span>
        <GmudVinculada chamado={chamado} />
      </td>
      <td className={cn(CELULA, SO_A_PARTIR_DE_LARGA)}>
        <RamoDaArvore caminho={chamado.entidade} />
      </td>
      <td className={cn(CELULA, SO_EM_TELA_MUITO_LARGA)}>
        <RamoDaArvore caminho={chamado.grupo} />
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap')}>
        <EtiquetaDeSla situacao={chamado.situacaoSla} rotulo={chamado.situacaoSlaRotulo} />
        <BarraDeSla
          abertoEm={chamado.abertoEm}
          prazoSla={chamado.prazoSla}
          fechadoEm={chamado.fechadoEm}
          situacao={chamado.situacaoSla}
        />
      </td>
      <td className={cn(CELULA, SO_A_PARTIR_DE_MEDIA, 'whitespace-nowrap tabular-nums')}>
        <time dateTime={chamado.abertoEm} title={dataHoraPorExtenso(chamado.abertoEm)}>
          {data(chamado.abertoEm)}
        </time>
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap tabular-nums')}>
        <Fechamento fechadoEm={chamado.fechadoEm} />
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap')}>
        <Julgamento julgado={chamado.julgado} />
      </td>
      <td className={cn(CELULA, SO_EM_TELA_MUITO_LARGA)}>
        <EtiquetaDeProcedencia origem={chamado.origem} curta />
      </td>
    </tr>
  )
}
